import { logger } from '@/shared/utils/logger';

export type BindErrorCode =
	| 'INVALID_PASSWORD'
	| 'INVALID_RFC'
	| 'RFC_NOT_REGISTERED'
	| 'NO_CIEC'
	| 'BAD_KEY_PASSWORD'
	| 'KEY_CERT_MISMATCH'
	| 'INVALID_CERT'
	| 'ALREADY_BOUND'
	| 'RFC_BOUND_TO_OTHER_USER'
	| 'CERT_REVOKED'
	| 'SAT_UNAVAILABLE';

export type BindErrorAction =
	| 'retry'
	| 'highlight_password'
	| 'refresh'
	| 'contact_support'
	| 'contact_support_cert';

interface BindErrorEntry {
	message: string;
	action: BindErrorAction;
}

const BIND_ERROR_MAP: Record<BindErrorCode, BindErrorEntry> = {
	INVALID_PASSWORD: {
		message:
			'Tu contraseña del SAT es incorrecta.',
		action: 'highlight_password',
	},
	INVALID_RFC: {
		message: 'El RFC no tiene el formato correcto. Revísalo e intenta de nuevo.',
		action: 'retry',
	},
	RFC_NOT_REGISTERED: {
		message: 'Este RFC no está registrado en el SAT.',
		action: 'retry',
	},
	NO_CIEC: {
		message: 'Tu RFC no tiene una contraseña del SAT (CIEC) dada de alta.',
		action: 'contact_support',
	},
	BAD_KEY_PASSWORD: {
		message: 'La contraseña de tu clave privada es incorrecta. Intenta de nuevo.',
		action: 'highlight_password',
	},
	KEY_CERT_MISMATCH: {
		message:
			'Los archivos .cer y .key no coinciden. Asegúrate de subir los archivos de la misma e.firma.',
		action: 'retry',
	},
	INVALID_CERT: {
		message: 'El certificado no es válido. Verifica que el archivo .cer sea correcto.',
		action: 'retry',
	},
	ALREADY_BOUND: {
		message: 'Ya tienes una cuenta del SAT vinculada.',
		action: 'refresh',
	},
	RFC_BOUND_TO_OTHER_USER: {
		message:
			'Este RFC ya está vinculado con otro número de teléfono. Si crees que es un error, contacta a soporte.',
		action: 'contact_support',
	},
	CERT_REVOKED: {
		message:
			'Tu certificado ya no es válido. Si trabajas con plataformas como Uber o Rappi, es posible que hayan generado una nueva e.firma por ti. Contacta a soporte para ayuda.',
		action: 'contact_support_cert',
	},
	SAT_UNAVAILABLE: {
		message:
			'Estamos presentando problemas al conectarnos con el SAT. Vuelve a intentar en unos minutos. Si el error persiste, contacta a soporte.',
		action: 'contact_support',
	},
};

export const GENERIC_BIND_ERROR: BindErrorEntry = {
	message:
		'No pudimos conectar tu e.firma. Verifica tus archivos y tu contraseña, e inténtalo de nuevo.',
	action: 'retry',
};

// CIEC credential failures where we surface an ASAT hand-off: a specialist can
// fix the SAT access problem (wrong password, malformed / unregistered RFC, no
// CIEC on file) on the user's behalf.
const CREDENTIAL_HELP_CODES: BindErrorCode[] = [
	'INVALID_PASSWORD',
	'INVALID_RFC',
	'RFC_NOT_REGISTERED',
	'NO_CIEC',
];

export const offersCredentialHelp = (
	errorCode?: BindErrorCode | string | null,
): boolean =>
	!!errorCode && CREDENTIAL_HELP_CODES.includes(errorCode as BindErrorCode);

const RAW_ERROR_PATTERNS: { test: RegExp; entry: BindErrorEntry }[] = [
	{
		test: /pkcs\s?#?\s?7|pkcs\s?#?\s?8|remove padding|invalid (pkcs\s?#?\s?7 )?padding|decrypt(ing)? (the )?(private )?key|bad decrypt|cipherfinal|wrong password/i,
		entry: {
			message:
				'La contraseña de tu clave privada es incorrecta. Verifícala e intenta de nuevo.',
			action: 'highlight_password',
		},
	},
	{
		// La CIEC ES la contraseña del SAT: el backend la nombra así en crudo
		// ("CIEC incorrecta") y sin esta línea el error caía al genérico —
		// mensaje del backend tal cual y sin campo dueño.
		test: /\bciec\b.*(incorrect|inv[aá]lid|err[oó]nea)|(incorrect|inv[aá]lid|err[oó]nea).*\bciec\b/i,
		entry: BIND_ERROR_MAP.INVALID_PASSWORD,
	},
	{
		test: /password de la fiel.*incorrect|contrase[nñ]a.*(incorrect|inv[aá]lid|err[oó]nea)|password.*(incorrect|invalid)/i,
		entry: {
			message:
				'La contraseña ingresada es incorrecta. Verifica mayúsculas, minúsculas y números, e inténtalo de nuevo.',
			action: 'highlight_password',
		},
	},
	{
		test: /no coinciden|key.?cert.?mismatch|(llave|key).*no corresponde|no corresponde.*(certificado|cert)/i,
		entry: {
			message:
				'Los archivos .cer y .key no coinciden. Asegúrate de subir los archivos de la misma e.firma.',
			action: 'retry',
		},
	},
	{
		test: /certificad[oa].*(no.*v[aá]lid|inv[aá]lid)|invalid cert/i,
		entry: {
			message:
				'El certificado no es válido. Verifica que el archivo .cer sea correcto.',
			action: 'retry',
		},
	},
];

const TECHNICAL_ERROR_PATTERN =
	/[a-z]{3,}:\s.+:\s|pkcs|padding|decrypt|cipher|\bx509\b|asn1|unmarshal|panic|runtime error|nil pointer|0x[0-9a-f]{2,}|\bbyte \d|encryptedprivatekey|\bder\b|\bpem\b|undefined|typeerror/i;

export const matchRawBindError = (
	rawMessage?: string | null,
): BindErrorEntry | null => {
	if (!rawMessage) return null;
	for (const { test, entry } of RAW_ERROR_PATTERNS) {
		if (test.test(rawMessage)) return entry;
	}
	return null;
};

export const isTechnicalError = (rawMessage?: string | null): boolean =>
	!!rawMessage && TECHNICAL_ERROR_PATTERN.test(rawMessage);

const logUnmappedBackendError = (rawMessage?: string | null): void => {
	if (rawMessage) {
		logger.warn('[bindError] unmapped backend error:', rawMessage);
	}
};

export const extractBackendErrorMessage = (
	error: unknown,
): string | undefined => {
	const e = error as {
		response?: {
			data?: { detail?: { msg?: string }; message?: string; msg?: string };
		};
		message?: string;
	};
	return (
		e?.response?.data?.detail?.msg ??
		e?.response?.data?.message ??
		e?.response?.data?.msg ??
		e?.message
	);
};

export const resolveBindError = (
	errorCode?: string,
	rawMessage?: string,
): BindErrorEntry => {
	if (errorCode && errorCode in BIND_ERROR_MAP) {
		return BIND_ERROR_MAP[errorCode as BindErrorCode];
	}
	const matched = matchRawBindError(rawMessage);
	if (matched) return matched;
	if (rawMessage && !isTechnicalError(rawMessage)) {
		return { message: rawMessage, action: 'retry' };
	}
	logUnmappedBackendError(rawMessage);
	return GENERIC_BIND_ERROR;
};
